import { getFormErrorMessage } from "@core/utility/get-error-message";
import { CardCategoryFormRule, CardCategoryFormSchema } from "@factory/form/form.rule";
import { useFormDefaultCardCategory } from "@factory/form/form-default-card-category.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseCardCategoryModel } from "@factory/model/query-model-card-category";
import { 
  useQyDeleteCardCategory, 
  useQyGetCardCategoryById, 
  useQyUpdateCardCategory} from "@factory/query/card-category.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditCardCategory() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultCardCategory();
  const navigate = useNavigate();
  const { mapUpdateCardCategory, mapDeleteCardCategory } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { cardCategoryId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID CardCategory
  const handleGetApiSuccess = (data: GetResponseCardCategoryModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
      setValue("tenant", responseData?.tenant || "");
      setValue("expirationYears", responseData?.expiration_years);
  };
  const {
    data: cardCategoryResponse,
    isLoading,
    isError: cardCategoryError,
  } = useQyGetCardCategoryById(cardCategoryId || "", handleGetApiSuccess);
  const cardCategoryData = cardCategoryResponse?.data?.[0];

  // API UPDATE CardCategory
  const handleUpdateApiSuccess = () => {
    showSuccess("CardCategory updated successfully");
  };
  const { mutate: updateCardCategory, isLoading: isEditLoading } = useQyUpdateCardCategory(
    handleUpdateApiSuccess
  );

  // API DELETE CardCategory
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("CardCategory deleted successfully");
  };
  const { mutate: deleteCardCategory } = useQyDeleteCardCategory(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<CardCategoryFormSchema>({
    defaultValues: getDefault(cardCategoryData),
    resolver: zodResolver(CardCategoryFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: CardCategoryFormSchema) => {
    if (!cardCategoryData || !cardCategoryData.code) {
      throw new Error("No cardCategory data");
    }

    const formData = mapUpdateCardCategory(form, cardCategoryData.code);
    updateCardCategory(formData);
  };
  const handleValidateError = (err: FieldErrors<CardCategoryFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!cardCategoryData || !cardCategoryData.code) {
      throw new Error("No cardCategory data found");
    }

    const form = mapDeleteCardCategory(cardCategoryData.code);
    deleteCardCategory(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    cardCategoryId,
    cardCategoryData,
    isLoading,
    cardCategoryError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}